import { clientLogger } from '@/lib/clientLogger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, Edit3, Package, Check, ChevronDown, ChevronUp, Search, RotateCcw } from 'lucide-react';
import { InvoiceItemDto, JobItemOperationDTO, InvoiceItemAutocompleteDto, CatalogItemDTO } from '@/types';
import { apiService } from '@/lib/api';
import { calculateSubtotal, calculateDiscount, calculateTax, calculateTotal } from '@/lib/invoiceCalculations';
import { EditableField } from './EditableField';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import { NumericInput } from './NumericInput';
import { ItemEditorDialog, PriceBookPanel, BottomSheet, OptionSheet, usePhoneWidth } from './ItemEditorDialog';

interface JobItemsSectionProps {
    items: InvoiceItemDto[];
    companyId?: string;
    taxRate?: number;
    defaultItemTaxable?: boolean;
    onItemsChange: (operations: JobItemOperationDTO[]) => Promise<void>;
    isEditable?: boolean;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    estimate?: string;
    onEstimateChange?: (estimate: string) => Promise<void>;
}

interface ItemFormData {
    id?: string;
    name: string;
    description: string;
    price: number;
    quantity: number;
    discount: number;
    discountType: 'Amount' | 'Percent';
    taxable: boolean;
}

type DiscountMode = 'None' | 'Percent' | 'Amount';

const DISCOUNT_OPTIONS: { value: DiscountMode; label: string }[] = [
    { value: 'None', label: 'None' },
    { value: 'Percent', label: 'Percentage (%)' },
    { value: 'Amount', label: 'Fixed ($)' },
];

const EMPTY_ITEM: ItemFormData = {
    name: '',
    description: '',
    price: 0,
    quantity: 1,
    discount: 0,
    discountType: 'Amount',
    taxable: true
};

export function JobItemsSection({
    items,
    companyId,
    taxRate = 0,
    defaultItemTaxable = true,
    onItemsChange,
    isEditable = true,
    collapsible = false,
    defaultCollapsed = false,
    estimate,
    onEstimateChange
}: JobItemsSectionProps) {
    const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
    const [isAddingItem, setIsAddingItem] = useState(false);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [formData, setFormData] = useState<ItemFormData>(EMPTY_ITEM);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [itemPendingDelete, setItemPendingDelete] = useState<(InvoiceItemDto & { id?: string }) | null>(null);

    // Phone layout of the item editor
    const isPhone = usePhoneWidth();
    const [priceBookOpen, setPriceBookOpen] = useState(false);
    const [discountSheetOpen, setDiscountSheetOpen] = useState(false);
    // A discount type was picked but no amount typed yet (an amount alone also counts as chosen).
    const [discountEnabled, setDiscountEnabled] = useState(false);

    // Calculations
    const subtotal = calculateSubtotal(items);
    const discount = calculateDiscount(items);
    const tax = calculateTax(items, taxRate);
    const total = calculateTotal(items, taxRate);

    // Autocomplete state
    const [suggestions, setSuggestions] = useState<InvoiceItemAutocompleteDto[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const nameInputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    // Name filled in from the Price Book or a suggestion. Suggestions only show for typed names.
    const autoFilledNameRef = useRef<string | null>(null);

    // Debounced search for autocomplete
    useEffect(() => {
        if (!companyId || !formData.name || formData.name.length < 2 || formData.name === autoFilledNameRef.current) {
            setSuggestions([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                const results = await apiService.searchInvoiceItems(formData.name);
                setSuggestions(results);
                setShowSuggestions(results.length > 0);
            } catch (error) {
                clientLogger.error('Failed to fetch suggestions:', error);
                setSuggestions([]);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [formData.name, companyId]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const validateForm = useCallback((data: ItemFormData): Record<string, string> => {
        const newErrors: Record<string, string> = {};
        if (!data.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (data.price < 0) {
            newErrors.price = 'Price must be positive';
        }
        if (data.quantity <= 0) {
            newErrors.quantity = 'Quantity must be greater than 0';
        }
        if (data.discount < 0) {
            newErrors.discount = 'Discount cannot be negative';
        }
        if (data.discountType === 'Percent' && data.discount > 100) {
            newErrors.discount = 'Percent discount cannot exceed 100';
        }
        return newErrors;
    }, []);

    const handleAddItem = () => {
        setFormData({
            ...EMPTY_ITEM,
            taxable: defaultItemTaxable
        });
        setErrors({});
        setDiscountEnabled(false);
        setIsAddingItem(true);
        setEditingItemId(null);
        setTimeout(() => nameInputRef.current?.focus(), 50);
    };

    const handleEditItem = (item: InvoiceItemDto & { id?: string }, index: number) => {
        // Existing name: no catalog suggestions until the name is changed.
        autoFilledNameRef.current = item.name;
        setSuggestions([]);
        setShowSuggestions(false);
        setFormData({
            id: item.id || `index-${index}`,
            name: item.name,
            description: item.description || '',
            price: item.price,
            quantity: item.quantity,
            discount: item.discount,
            discountType: item.discount_type || 'Amount',
            taxable: item.taxable ?? true
        });
        setErrors({});
        setDiscountEnabled(false);
        setEditingItemId(item.id || `index-${index}`);
        setIsAddingItem(false);
        // Phones: don't pop the keyboard up just from opening an item; it opens when a field is tapped.
        if (!isPhone) setTimeout(() => nameInputRef.current?.focus(), 50);
    };

    const handleCancelEdit = () => {
        setFormData(EMPTY_ITEM);
        setErrors({});
        setIsAddingItem(false);
        setEditingItemId(null);
    };

    // Empty the Add Item form without closing the window.
    const handleClearForm = () => {
        autoFilledNameRef.current = null;
        setFormData({ ...EMPTY_ITEM, taxable: defaultItemTaxable });
        setErrors({});
        setDiscountEnabled(false);
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const handleDiscountModeChange = (mode: DiscountMode) => {
        if (mode === 'None') {
            setDiscountEnabled(false);
            setFormData(prev => ({ ...prev, discount: 0, discountType: 'Amount' }));
        } else {
            setDiscountEnabled(true);
            setFormData(prev => ({
                ...prev,
                discountType: mode,
                discount: mode === 'Percent' ? Math.min(prev.discount, 100) : prev.discount
            }));
        }
        if (errors.discount) setErrors(prev => ({ ...prev, discount: '' }));
    };

    const handleSaveItem = async () => {
        const validationErrors = validateForm(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSaving(true);
        try {
            const itemData: InvoiceItemDto & { id?: string } = {
                id: formData.id,
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                price: formData.price,
                quantity: formData.quantity,
                discount: formData.discount,
                discount_type: formData.discountType,
                taxable: formData.taxable
            };

            if (isAddingItem) {
                await onItemsChange([{ operation: 'Add', item: itemData }]);
            } else if (editingItemId) {
                await onItemsChange([{ operation: 'Edit', item: itemData }]);
            }

            handleCancelEdit();
        } catch (error) {
            clientLogger.error('Failed to save item:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const commitDeleteItem = async (item: InvoiceItemDto & { id?: string }) => {
        if (!item.id) return;

        setIsSaving(true);
        try {
            await onItemsChange([{ operation: 'Remove', item: { ...item, id: item.id } }]);
        } catch (error) {
            clientLogger.error('Failed to delete item:', error);
            throw error;
        } finally {
            setIsSaving(false);
        }
    };

    const lineItemDeleteDialog = (
        <ConfirmDialog
            isOpen={!!itemPendingDelete}
            onClose={() => setItemPendingDelete(null)}
            onConfirm={async () => {
                if (itemPendingDelete) {
                    await commitDeleteItem(itemPendingDelete);
                }
            }}
            isLoading={isSaving}
            title="Remove line item?"
            message={
                <div className="space-y-2">
                    <span>{destructiveCopy.removeLineItem}</span>
                    <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
                </div>
            }
            confirmText="Remove"
        />
    );

    const handleInputChange = (field: keyof ItemFormData, value: string | number | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleSelectSuggestion = async (suggestion: InvoiceItemAutocompleteDto) => {
        try {
            const fullItem = await apiService.getInvoiceItemById(suggestion.id);
            autoFilledNameRef.current = fullItem.name;
            setFormData(prev => ({
                ...prev,
                name: fullItem.name,
                description: fullItem.description || '',
                price: fullItem.price,
                quantity: fullItem.quantity || 1,
                discount: fullItem.discount || 0,
                discountType: fullItem.discount_type || 'Amount',
                taxable: fullItem.taxable ?? true
            }));
        } catch (error) {
            // Fallback to basic info
            autoFilledNameRef.current = suggestion.name;
            setFormData(prev => ({
                ...prev,
                name: suggestion.name,
                price: suggestion.price
            }));
        }
        setShowSuggestions(false);
        setSuggestions([]);
    };

    // Price Book pick: fill the form so the item can be adjusted before adding.
    const handlePickCatalogItem = (item: CatalogItemDTO) => {
        autoFilledNameRef.current = item.name;
        setFormData(prev => ({
            ...prev,
            name: item.name,
            description: item.description || '',
            price: item.price ?? 0,
            taxable: item.taxable
        }));
        setErrors({});
        setShowSuggestions(false);
        setSuggestions([]);
        setPriceBookOpen(false);
        // Bring the filled-in item into view. (Wider screens don't scroll this area, so nothing moves there.)
        nameInputRef.current?.closest('[data-item-editor-body]')?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions || suggestions.length === 0) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex(prev => Math.max(prev - 1, 0));
        } else if (event.key === 'Enter' && highlightedIndex >= 0) {
            event.preventDefault();
            handleSelectSuggestion(suggestions[highlightedIndex]);
        } else if (event.key === 'Escape') {
            // Close the suggestions only, not the Add Item window around them.
            event.preventDefault();
            setShowSuggestions(false);
        }
    };

    const isEditing = isAddingItem || editingItemId !== null;

    const addItemDialog = isAddingItem && (
        <ItemEditorDialog
            title="Add Item"
            onClose={handleCancelEdit}
            // Phones close the window with the X in the header instead.
            footer={!isPhone &&
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="flex-1 sm:flex-none sm:min-w-[140px] px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    {items.length > 0 && (
                        <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="flex-1 sm:flex-none sm:min-w-[140px] flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-xl shadow-sm hover:bg-green-700 transition-colors"
                        >
                            <Check className="h-4 w-4" />
                            Done
                        </button>
                    )}
                </div>
            }
            summary={renderAddedSummary()}
            priceBook={companyId && !isPhone && (
                <PriceBookPanel
                    companyId={companyId}
                    onPick={handlePickCatalogItem}
                    selectedName={formData.name}
                />
            )}
        >
            {renderDialogForm()}
        </ItemEditorDialog>
    );

    // Edit uses the same window as Add Item, with just the form.
    const editingItem = editingItemId !== null
        ? (items as (InvoiceItemDto & { id?: string })[]).find((it, i) => (it.id || `index-${i}`) === editingItemId)
        : undefined;
    const editItemDialog = editingItemId !== null && (
        <ItemEditorDialog
            title="Edit Item"
            onClose={handleCancelEdit}
            footer={
                <div className="flex justify-end gap-3">
                    {/* Phones have no delete icon on the item rows, so it lives here. */}
                    {isPhone && editingItem && (
                        <button
                            type="button"
                            onClick={() => {
                                setItemPendingDelete(editingItem);
                                handleCancelEdit();
                            }}
                            disabled={isSaving}
                            className="flex items-center justify-center rounded-xl border border-red-200 bg-white px-3 py-2.5 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                            aria-label="Delete item"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="flex-1 sm:flex-none sm:min-w-[140px] px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveItem}
                        disabled={isSaving}
                        className="flex-1 sm:flex-none sm:min-w-[140px] flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl shadow-sm hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                        <Check className="h-4 w-4" />
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            }
        >
            {renderDialogForm()}
        </ItemEditorDialog>
    );

    // Collapsible mode (for mobile)
    if (collapsible) {
        return (
            <>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Collapsed Header */}
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full p-3 flex items-center justify-between bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                >
                    <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-blue-600" />
                            <span className="font-semibold text-gray-900">Items</span>
                            {estimate != null && estimate !== '' && (
                                <span className="text-gray-500">(Est: {estimate})</span>
                            )}
                            <span className="text-gray-500">({items.length})</span>
                        </div>
                        {!isExpanded && items.length > 0 && (
                            <>
                                <div className="w-px h-4 bg-gray-300"></div>
                                <span className="font-semibold text-gray-900">Total: ${total.toFixed(2)}</span>
                            </>
                        )}
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                    <div className="border-t border-gray-100 p-3 space-y-3">
                        {/* Estimate field - editable when onEstimateChange provided */}
                        {estimate !== undefined && (
                            <div className="flex items-center justify-between py-1">
                                <span className="text-xs font-medium text-gray-600">Estimate</span>
                                {onEstimateChange && isEditable ? (
                                    <EditableField
                                        value={estimate}
                                        type="text"
                                        onSave={async (val) => {
                                            await onEstimateChange(String(val ?? ''));
                                        }}
                                        inline
                                        alwaysShowEdit={collapsible}
                                        renderValue={(val) => (
                                            <span className="text-sm font-bold text-gray-900">
                                                {String(val ?? '')}
                                            </span>
                                        )}
                                    />
                                ) : (
                                    <span className="text-sm font-bold text-gray-900">{estimate}</span>
                                )}
                            </div>
                        )}
                        {/* Add Button (at top when expanded) */}
                        {isEditable && !isEditing && (
                            <button
                                type="button"
                                onClick={handleAddItem}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                            >
                                <Plus className="h-4 w-4" />
                                Add Item
                            </button>
                        )}

                        {/* Items List */}
                        {items.length > 0 && (
                            renderItemsTable()
                        )}

                        {/* Empty State */}
                        {items.length === 0 && !isAddingItem && (
                            <p className="text-sm text-gray-500 text-center py-2">No items added</p>
                        )}

                        {/* Summary Section */}
                        {items.length > 0 && (
                            <div className="mt-4 border-t border-gray-200 pt-3 space-y-1">
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>Subtotal</span>
                                    <span>${subtotal.toFixed(2)}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-sm text-green-600">
                                        <span>Discount</span>
                                        <span>-${discount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>Tax ({taxRate}%)</span>
                                    <span>${tax.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-base font-bold text-gray-900 border-t border-gray-200 pt-2 mt-2">
                                    <span>Total</span>
                                    <span>${total.toFixed(2)}</span>
                                </div>
                            </div>
                        )}


                    </div>
                )}
            </div>
            {lineItemDeleteDialog}
            {addItemDialog}
            {editItemDialog}
            </>
        );
    }

    // Non-collapsible mode (for desktop)
    return (
        <>
        <div className={`bg-gray-50 rounded-lg ${isExpanded ? 'p-4' : 'p-3'} transition-all duration-200`}>
            {/* Header */}
            <div
                className={`flex items-center justify-between cursor-pointer ${isExpanded ? 'mb-3' : ''}`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-4">
                    <h3 className="text-m font-semibold text-gray-900 flex items-center gap-2">
                        <Package className="h-5 w-5 text-blue-600" />
                        Items
                        {estimate != null && estimate !== '' && (
                            <span className="text-sm font-normal text-gray-500">(Est: {estimate})</span>
                        )}
                        {items.length > 0 && (
                            <span className="text-sm font-normal text-gray-500">({items.length})</span>
                        )}
                    </h3>
                    {!isExpanded && items.length > 0 && (
                        <div className="flex items-center gap-3 text-sm">
                            <div className="w-px h-5 bg-gray-300"></div>
                            <span className="font-semibold text-gray-900">Total: ${total.toFixed(2)}</span>
                        </div>
                    )}
                </div>

                {/* Right side - Add button and Chevron */}
                <div className="flex items-center gap-2">
                    {isEditable && !isEditing && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddItem();
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        >
                            <Plus className="h-3.5 w-3.5" />
                            Add Item
                        </button>
                    )}
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <>
                    {/* Estimate field - editable when onEstimateChange provided */}
                    {estimate !== undefined && (
                        <div className="flex items-center justify-between mb-3 py-1">
                            <span className="text-sm font-medium text-gray-600">Estimate</span>
                            {onEstimateChange && isEditable ? (
                                <EditableField
                                    value={estimate}
                                    type="text"
                                    onSave={async (val) => {
                                        await onEstimateChange(String(val ?? ''));
                                    }}
                                    inline
                                    renderValue={(val) => (
                                        <span className="text-m font-bold text-gray-900">
                                            {String(val ?? '')}
                                        </span>
                                    )}
                                />
                            ) : (
                                <span className="text-m font-bold text-gray-900">{estimate}</span>
                            )}
                        </div>
                    )}

                    {/* Items List */}
                    {items.length > 0 && (
                        <div className="mb-3">{renderItemsTable()}</div>
                    )}

                    {/* Empty State */}
                    {items.length === 0 && !isAddingItem && (
                        <p className="text-sm text-gray-500 text-center py-3">No items added</p>
                    )}

                    {/* Summary Section */}
                    {items.length > 0 && (
                        <div className="mt-4 border-t border-gray-200 pt-3 space-y-1 px-2">
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Subtotal</span>
                                <span>${subtotal.toFixed(2)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-sm text-green-600">
                                    <span>Discount</span>
                                    <span>-${discount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Tax ({taxRate}%)</span>
                                <span>${tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-base font-bold text-gray-900 border-t border-gray-200 pt-2 mt-2">
                                <span>Total</span>
                                <span>${total.toFixed(2)}</span>
                            </div>
                        </div>
                    )}


                </>
            )}
        </div>
        {lineItemDeleteDialog}
        {addItemDialog}
        {editItemDialog}
        </>
    );

    function renderItemsTable() {
        if (isPhone) return renderPhoneItemList();
        const cols = 'grid grid-cols-[minmax(0,1fr)_2rem_4rem_4.25rem_auto] sm:grid-cols-[minmax(0,1fr)_3rem_5rem_5rem_auto] items-center gap-1.5 sm:gap-3';
        const showActions = isEditable && !isEditing;
        return (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Column headers */}
                <div className={`${cols} px-2.5 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500`}>
                    <span>Item</span>
                    <span className="text-center">QTY</span>
                    <span className="text-right">Price</span>
                    <span className="text-right">Total</span>
                    <span className={showActions ? 'w-[3.75rem]' : ''} />
                </div>
                <div className="divide-y divide-gray-100">
                    {items.map((item, index) => {
                        const itemWithId = item as InvoiceItemDto & { id?: string };

                        const lineTotal = item.discount_type === 'Percent'
                            ? item.price * item.quantity * (1 - item.discount / 100)
                            : (item.price - item.discount) * item.quantity;

                        return (
                            <div key={itemWithId.id || index} className={`${cols} px-2.5 py-2.5 hover:bg-gray-50/60 transition-colors`}>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 break-words">{item.name}</p>
                                    {item.description && (
                                        <p className="text-xs text-gray-500 line-clamp-1">{item.description}</p>
                                    )}
                                    {(item.discount > 0 || item.taxable === false) && (
                                        <div className="flex flex-wrap gap-x-2 mt-0.5 text-xs">
                                            {item.discount > 0 && (
                                                <span className="text-green-600">
                                                    {item.discount_type === 'Percent'
                                                        ? `-${item.discount}%`
                                                        : `-$${(item.discount * item.quantity).toFixed(2)}`}
                                                </span>
                                            )}
                                            {item.taxable === false && (
                                                <span className="text-indigo-400">No Tax</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <span className="text-sm text-gray-900 text-center">{item.quantity}</span>
                                <span className="text-sm text-gray-900 text-right">${item.price.toFixed(2)}</span>
                                <span className="text-sm font-semibold text-green-600 text-right">${lineTotal.toFixed(2)}</span>
                                {showActions ? (
                                    <div className="flex items-center justify-end gap-0.5 w-[3.75rem]">
                                        <button
                                            type="button"
                                            onClick={() => handleEditItem(itemWithId, index)}
                                            className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                                            aria-label="Edit item"
                                        >
                                            <Edit3 className="h-4 w-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setItemPendingDelete(itemWithId)}
                                            disabled={isSaving}
                                            className="p-1.5 rounded-md text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
                                            aria-label="Delete item"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <span />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Phones: name on top, "qty x price" under it, line total on the right. Tap a line to edit.
    function renderPhoneItemList() {
        const canEdit = isEditable && !isEditing;
        return (
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                {items.map((item, index) => {
                    const itemWithId = item as InvoiceItemDto & { id?: string };
                    const lineTotal = item.discount_type === 'Percent'
                        ? item.price * item.quantity * (1 - item.discount / 100)
                        : (item.price - item.discount) * item.quantity;
                    const content = (
                        <>
                            <span className="min-w-0 flex-1">
                                <span className="block text-[15px] font-medium text-gray-900 break-words">{item.name}</span>
                                <span className="mt-0.5 block text-sm text-gray-500">
                                    {item.quantity} x ${item.price.toFixed(2)}
                                    {item.discount > 0 && (
                                        <span className="text-green-600">
                                            {' · '}
                                            {item.discount_type === 'Percent' ? `-${item.discount}%` : `-$${(item.discount * item.quantity).toFixed(2)}`}
                                        </span>
                                    )}
                                    {item.taxable === false && <span className="text-indigo-400"> · No Tax</span>}
                                </span>
                            </span>
                            <span className="shrink-0 text-[15px] font-semibold text-gray-900">${lineTotal.toFixed(2)}</span>
                        </>
                    );
                    return canEdit ? (
                        <button
                            key={itemWithId.id || index}
                            type="button"
                            onClick={() => handleEditItem(itemWithId, index)}
                            className="flex w-full items-center gap-3 px-3 py-3 text-left active:bg-gray-50"
                        >
                            {content}
                        </button>
                    ) : (
                        <div key={itemWithId.id || index} className="flex items-center gap-3 px-3 py-3">
                            {content}
                        </div>
                    );
                })}
            </div>
        );
    }

    function renderSuggestions() {
        if (!showSuggestions || suggestions.length === 0) return null;
        return (
            <div
                ref={suggestionsRef}
                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
                {suggestions.map((suggestion, idx) => (
                    <button
                        key={suggestion.id}
                        type="button"
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex justify-between items-center ${highlightedIndex === idx ? 'bg-blue-50' : ''}`}
                    >
                        <span className="flex items-center gap-2">
                            <span className="font-medium">{suggestion.name}</span>
                            {suggestion.source === 'catalog' && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Catalog</span>
                            )}
                        </span>
                        <span className="text-gray-500">${suggestion.price.toFixed(2)}</span>
                    </button>
                ))}
            </div>
        );
    }

    // Items already on the job, shown under the form so each new item lands in view.
    function renderAddedSummary() {
        return (
            <div className="rounded-xl border border-gray-200">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">
                        Added Items{items.length > 0 && <span className="font-normal text-gray-500"> ({items.length})</span>}
                    </h3>
                </div>
                {items.length === 0 ? (
                    <p className="px-4 py-5 text-center text-sm text-gray-500">No items added yet</p>
                ) : (
                    <>
                        <ul className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
                            {items.map((item, index) => {
                                const itemWithId = item as InvoiceItemDto & { id?: string };
                                const lineTotal = item.discount_type === 'Percent'
                                    ? item.price * item.quantity * (1 - item.discount / 100)
                                    : (item.price - item.discount) * item.quantity;
                                return (
                                    <li key={itemWithId.id || index} className="flex items-center justify-between gap-3 px-4 py-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                                            <p className="text-xs text-gray-500">
                                                {item.quantity} × ${item.price.toFixed(2)}
                                                {item.discount > 0 && (
                                                    <span className="text-green-600">
                                                        {' · '}
                                                        {item.discount_type === 'Percent' ? `-${item.discount}%` : `-$${(item.discount * item.quantity).toFixed(2)}`}
                                                    </span>
                                                )}
                                                {item.taxable === false && ' · No Tax'}
                                            </p>
                                        </div>
                                        <span className="shrink-0 text-sm font-semibold text-gray-900">${lineTotal.toFixed(2)}</span>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="space-y-1 border-t border-gray-200 bg-gray-50 px-4 py-3 rounded-b-xl">
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Subtotal</span>
                                <span>${subtotal.toFixed(2)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-sm text-green-600">
                                    <span>Discount</span>
                                    <span>-${discount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Tax ({taxRate}%)</span>
                                <span>${tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between border-t border-gray-200 pt-2 mt-1 text-base font-bold text-gray-900">
                                <span>Total</span>
                                <span>${total.toFixed(2)}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // The floating Add Item / Edit Item form.
    function renderDialogForm() {
        const lineTotal = formData.discountType === 'Percent'
            ? formData.price * formData.quantity * (1 - formData.discount / 100)
            : (formData.price - formData.discount) * formData.quantity;
        return renderItemFields(lineTotal);
    }

    // Grouped rows with the field names inside them and the discount type picked from a sheet.
    // On phones a search button on the name opens the Price Book; wider screens show it beside the form.
    function renderItemFields(lineTotal: number) {
        const rowClass = 'bg-white rounded-xl px-4 focus-within:ring-2 focus-within:ring-primary-500';
        const bareInput = 'w-full min-w-0 bg-transparent border-0 p-0 text-base text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none';
        const discountMode: DiscountMode = discountEnabled || formData.discount > 0 ? formData.discountType : 'None';
        const discountLabel = DISCOUNT_OPTIONS.find(o => o.value === discountMode)?.label ?? 'None';

        return (
            <div className="space-y-4">
                <div className="space-y-2 rounded-2xl bg-gray-100 p-2">
                    {/* Name, with the Price Book behind the search button */}
                    <div className="relative">
                        <div className={`${rowClass} flex items-center gap-3 py-2 ${errors.name ? 'ring-2 ring-red-500' : ''}`}>
                            <input
                                ref={nameInputRef}
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                onKeyDown={handleNameKeyDown}
                                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                placeholder="Item name"
                                aria-label="Item name"
                                className={`${bareInput} py-2`}
                            />
                            {companyId && isPhone && (
                                <button
                                    type="button"
                                    onClick={() => setPriceBookOpen(true)}
                                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white shadow-sm hover:bg-primary-700 active:bg-primary-800 transition-colors"
                                    aria-label="Open Price Book"
                                >
                                    <Search className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                        {renderSuggestions()}
                    </div>
                    {errors.name && <p className="px-2 text-xs text-red-500">{errors.name}</p>}

                    {/* Description */}
                    <div className={`${rowClass} py-3`}>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Description (optional)"
                            aria-label="Description"
                            rows={3}
                            className={`${bareInput} resize-none`}
                        />
                    </div>

                    {/* Price and quantity side by side, names inside the boxes */}
                    <div className="grid grid-cols-2 bg-white rounded-xl divide-x divide-gray-200">
                        <label className={`block px-4 py-2.5 rounded-l-xl focus-within:ring-2 focus-within:ring-primary-500 ${errors.price ? 'ring-2 ring-red-500' : ''}`}>
                            <span className="block text-xs text-gray-500">Price</span>
                            <span className="flex items-center gap-1">
                                <span className="text-base text-gray-500">$</span>
                                <NumericInput
                                    value={formData.price}
                                    onChange={(val) => handleInputChange('price', val)}
                                    step="0.01"
                                    min="0"
                                    inputMode="decimal"
                                    className={bareInput}
                                />
                            </span>
                        </label>
                        <label className={`block px-4 py-2.5 rounded-r-xl focus-within:ring-2 focus-within:ring-primary-500 ${errors.quantity ? 'ring-2 ring-red-500' : ''}`}>
                            <span className="block text-xs text-gray-500">Quantity</span>
                            <NumericInput
                                value={formData.quantity}
                                onChange={(val) => handleInputChange('quantity', val)}
                                fallbackValue={1}
                                step="0.01"
                                min="0.01"
                                inputMode="decimal"
                                className={bareInput}
                            />
                        </label>
                    </div>
                    {(errors.price || errors.quantity) && (
                        <p className="px-2 text-xs text-red-500">{errors.price || errors.quantity}</p>
                    )}

                    {/* Discount type, picked from a sheet */}
                    <button
                        type="button"
                        onClick={() => setDiscountSheetOpen(true)}
                        className="flex w-full items-center justify-between bg-white rounded-xl px-4 py-4 text-left active:bg-gray-50"
                    >
                        <span className="text-base text-gray-900">Discount Type</span>
                        <span className="text-base font-medium text-primary-600">{discountLabel}</span>
                    </button>

                    {/* Discount amount, once a type is chosen */}
                    {discountMode !== 'None' && (
                        <label className={`block ${rowClass} py-2.5 ${errors.discount ? 'ring-2 ring-red-500' : ''}`}>
                            <span className="block text-xs text-gray-500">
                                {discountMode === 'Percent' ? 'Discount (%)' : 'Discount ($)'}
                            </span>
                            <span className="flex items-center gap-1">
                                {discountMode === 'Amount' && <span className="text-base text-gray-500">$</span>}
                                <NumericInput
                                    value={formData.discount}
                                    onChange={(val) => handleInputChange('discount', val)}
                                    step={discountMode === 'Percent' ? '1' : '0.01'}
                                    min="0"
                                    max={discountMode === 'Percent' ? '100' : undefined}
                                    inputMode="decimal"
                                    className={bareInput}
                                />
                                {discountMode === 'Percent' && <span className="text-base text-gray-500">%</span>}
                            </span>
                        </label>
                    )}
                    {errors.discount && <p className="px-2 text-xs text-red-500">{errors.discount}</p>}

                    {/* Taxable */}
                    <label className="flex items-center gap-3 bg-white rounded-xl px-4 py-4 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={formData.taxable}
                            onChange={(e) => handleInputChange('taxable', e.target.checked)}
                            className="h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-base text-gray-900">Taxable</span>
                    </label>
                </div>

                {renderLineTotalAndActions(lineTotal)}

                {companyId && isPhone && (
                    <BottomSheet
                        open={priceBookOpen}
                        onClose={() => setPriceBookOpen(false)}
                        title="Price Book"
                        tall
                    >
                        <PriceBookPanel
                            companyId={companyId}
                            onPick={handlePickCatalogItem}
                            selectedName={formData.name}
                            inSheet
                        />
                    </BottomSheet>
                )}
                <OptionSheet
                    open={discountSheetOpen}
                    onClose={() => setDiscountSheetOpen(false)}
                    options={DISCOUNT_OPTIONS}
                    value={discountMode}
                    onSelect={handleDiscountModeChange}
                />
            </div>
        );
    }

    function renderLineTotalAndActions(lineTotal: number) {
        return (
            <>
                {/* Line total */}
                <div className="flex items-center justify-between rounded-xl bg-gray-100 px-4 py-4">
                    <span className="text-sm font-medium text-gray-600">Line Total</span>
                    <span className="text-2xl font-bold text-gray-900">${Math.max(0, lineTotal).toFixed(2)}</span>
                </div>

                {isAddingItem && <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleClearForm}
                        disabled={isSaving}
                        className="flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        <RotateCcw className="h-4 w-4" />
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveItem}
                        disabled={isSaving}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-primary-600 rounded-xl shadow-sm hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                        <Check className="h-4 w-4" />
                        {isSaving ? 'Saving...' : 'Save Item'}
                    </button>
                </div>}
            </>
        );
    }
}
